import { useEffect, useState } from 'react';

import {
  AlertTriangle,
  PauseCircle,
  PlayCircle,
  ShieldAlert,
  ShieldX,
  X,
  XOctagon,
  type LucideIcon,
} from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { useAuth } from '@/nucleo/auth/useAuth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/ui/base/alert-dialog';
import { Button } from '@/ui/base/button';
import { Card } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';

import {
  ACOES_IRREVERSIVEIS,
  ACOES_QUE_EXIGEM_PARECER,
  MAXIMO_DE_ITENS_EM_LOTE,
  PALAVRA_DE_CONFIRMACAO,
  ROTULO_DA_ACAO,
  type AcaoEmLote,
  type Credenciado,
} from './contrato';

/**
 * As acoes que se aplicam a varios credenciados selecionados.
 *
 * A BARRA E FIXA NO TOPO (`sticky`) porque a selecao se faz descendo a tabela: sem
 * isso, quem marca a decima linha perde de vista quantos marcou e onde estao os botoes.
 *
 * TRES FREIOS, CADA UM CONTRA UM ERRO DIFERENTE:
 *
 * 1. TETO DE {@link MAXIMO_DE_ITENS_EM_LOTE} ITENS. "Selecionar todos desta página"
 *    algumas vezes acumula rapido, e suspender cem credenciados por engano e incidente,
 *    nao erro de digitacao. Acima do teto o botao fica indisponivel com o motivo dito.
 * 2. PARECER OBRIGATORIO nas acoes que restringem. E o que sobra na auditoria quando
 *    alguem perguntar, meses depois, por que aquele credenciado foi suspenso.
 * 3. PALAVRA DIGITADA nas irreversiveis. Caixa de "tem certeza?" se aceita no piloto
 *    automatico; digitar {@link PALAVRA_DE_CONFIRMACAO} obriga a parar.
 *
 * "CONFIRMAR E APLICAR" NAO CHAMA API NENHUMA — nenhuma tabela cadastral aceita
 * escrita hoje, e mudar a situacao de um credenciado e escrita (docs/ALVOS.md, Onda 2;
 * ver `comum/cadastroIndisponivel.ts`). Os tres freios acima continuam valendo porque
 * sao validacao de interface, pronta para quando a rota existir; so o passo final
 * mostra o aviso honesto em vez de fingir que aplicou a acao.
 */

interface AcaoDisponivel {
  acao: AcaoEmLote;
  icone: LucideIcon;
  variante: 'default' | 'destructive' | 'secondary';
}

const ACOES: readonly AcaoDisponivel[] = [
  { acao: 'SUSPENDER', icone: PauseCircle, variante: 'secondary' },
  { acao: 'REATIVAR', icone: PlayCircle, variante: 'default' },
  { acao: 'BLOQUEAR', icone: ShieldX, variante: 'destructive' },
  { acao: 'DESCREDENCIAR', icone: XOctagon, variante: 'destructive' },
];

/**
 * Quem pode descredenciar.
 *
 * Descredenciar encerra o vinculo do transportador com o programa — e a acao mais grave
 * da tela, e nao pertence a todo papel interno. A lista e a da gestao do DTP.
 *
 * ISTO E PROVISORIO E ESTA MARCADO COMO TAL: o modulo de descredenciamento e de fase
 * futura, e e ele que vai trazer a regra validada com o cliente. Ate lá vale errar para
 * o lado de exigir mais — e a guarda que conta e a da API e a do banco, nunca esta
 * (docs/AUTENTICACAO-E-ACESSO.md, secao 9).
 */
const PAPEIS_QUE_DESCREDENCIAM: readonly string[] = ['ADMIN', 'GESTOR_ADMIN', 'GESTOR'];

interface Confirmacao {
  acao: AcaoEmLote;
  exigeParecer: boolean;
  irreversivel: boolean;
}

interface BarraDeAcoesEmLoteProps {
  /** Identificadores selecionados, de todas as paginas visitadas. */
  selecionados: string[];
  /** As linhas da pagina atual — de onde sai a previa nominal do que vai mudar. */
  linhasDaPagina: readonly Credenciado[];
  aoLimpar: () => void;
}

export function BarraDeAcoesEmLote({
  selecionados,
  linhasDaPagina,
  aoLimpar,
}: BarraDeAcoesEmLoteProps) {
  const { usuario } = useAuth();

  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
  const [parecer, setParecer] = useState('');
  const [bloqueadoAte, setBloqueadoAte] = useState('');
  const [palavraDigitada, setPalavraDigitada] = useState('');

  // Fechar a confirmacao esvazia o formulario: parecer de uma acao nao vaza para a
  // proxima, que e como alguem bloqueia com a justificativa de uma suspensao.
  useEffect(() => {
    if (!confirmacao) {
      setParecer('');
      setBloqueadoAte('');
      setPalavraDigitada('');
    }
  }, [confirmacao]);

  if (selecionados.length === 0) {
    return null;
  }

  const podeDescredenciar = (usuario?.papeis ?? []).some((papel) =>
    PAPEIS_QUE_DESCREDENCIAM.includes(papel),
  );
  // O que o usuario nao pode fazer nao e renderizado — nao fica desabilitado
  // (CLAUDE.md, "Regras de interface", item 7).
  const acoesVisiveis = ACOES.filter((item) => item.acao !== 'DESCREDENCIAR' || podeDescredenciar);

  const excedeuTeto = selecionados.length > MAXIMO_DE_ITENS_EM_LOTE;

  /**
   * Previa do que vai mudar, com nome e situacao atual.
   *
   * Sai das linhas JA CARREGADAS, e nao de uma consulta nova: pedir os cem selecionados
   * de volta a API so para preencher uma lista de conferencia e uma ida a rede a troco de
   * nada. O preco e que selecao feita em paginas anteriores nao aparece nominalmente — e
   * a contagem total, essa, esta sempre certa.
   */
  const previa = linhasDaPagina.filter((linha) => selecionados.includes(linha.id));

  const liberado = (() => {
    if (!confirmacao) {
      return false;
    }
    if (confirmacao.exigeParecer && !parecer.trim()) {
      return false;
    }
    if (
      confirmacao.irreversivel &&
      palavraDigitada.trim().toUpperCase() !== PALAVRA_DE_CONFIRMACAO
    ) {
      return false;
    }
    return true;
  })();

  const confirmar = () => {
    if (!confirmacao || !liberado) {
      return;
    }

    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
    setConfirmacao(null);
  };

  return (
    <>
      {selecionados.length > 0 && (
        <Card className="border-primary/40 bg-primary/5 sticky top-2 z-30 flex flex-col items-start gap-3 px-4 py-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
              {selecionados.length}
            </span>
            credenciado(s) selecionado(s)
            {excedeuTeto && (
              <span className="text-destructive ml-2 inline-flex items-center gap-1 text-xs font-normal">
                <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
                acima do limite de {MAXIMO_DE_ITENS_EM_LOTE}
              </span>
            )}
          </div>

          <div className="flex-1" />

          <div className="flex flex-wrap gap-2">
            {acoesVisiveis.map(({ acao, icone: Icone, variante }) => (
              <Button
                key={acao}
                size="sm"
                variant={variante}
                onClick={() =>
                  setConfirmacao({
                    acao,
                    exigeParecer: ACOES_QUE_EXIGEM_PARECER.includes(acao),
                    irreversivel: ACOES_IRREVERSIVEIS.includes(acao),
                  })
                }
                disabled={excedeuTeto}
                title={
                  excedeuTeto
                    ? `Reduza a seleção para até ${MAXIMO_DE_ITENS_EM_LOTE} itens`
                    : undefined
                }
              >
                <Icone className="mr-1 h-4 w-4" aria-hidden="true" />
                {ROTULO_DA_ACAO[acao]}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={aoLimpar}>
              <X className="mr-1 h-4 w-4" aria-hidden="true" />
              Limpar seleção
            </Button>
          </div>
        </Card>
      )}

      <AlertDialog
        open={!!confirmacao}
        onOpenChange={(aberto) => {
          if (!aberto) {
            setConfirmacao(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle
                className={
                  confirmacao?.irreversivel ? 'text-destructive h-5 w-5' : 'text-warning h-5 w-5'
                }
                aria-hidden="true"
              />
              Confirmar: {confirmacao ? ROTULO_DA_ACAO[confirmacao.acao] : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              A ação valeria para <strong>{selecionados.length}</strong> credenciado(s).
            </AlertDialogDescription>
          </AlertDialogHeader>

          {previa.length > 0 && (
            <div className="border-border bg-muted/30 max-h-40 overflow-auto rounded border">
              <table className="w-full text-xs">
                <caption className="sr-only">
                  Credenciados selecionados nesta página, com a situação atual
                </caption>
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th scope="col" className="px-3 py-1.5 text-left font-medium">
                      CPF/CNPJ
                    </th>
                    <th scope="col" className="px-3 py-1.5 text-left font-medium">
                      Nome
                    </th>
                    <th scope="col" className="px-3 py-1.5 text-left font-medium">
                      Situação atual
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previa.map((linha) => (
                    <tr key={linha.id} className="border-border border-t">
                      <td className="px-3 py-1.5 font-mono">{linha.cpf_cnpj}</td>
                      <td className="max-w-[260px] truncate px-3 py-1.5">{linha.nome}</td>
                      <td className="text-muted-foreground px-3 py-1.5">{linha.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previa.length < selecionados.length && (
                <p className="text-muted-foreground border-border border-t px-3 py-1.5 text-[11px]">
                  {selecionados.length - previa.length} selecionado(s) em outras páginas não
                  aparecem nesta lista, mas entram na ação.
                </p>
              )}
            </div>
          )}

          {confirmacao?.exigeParecer && (
            <div className="space-y-2">
              <Label htmlFor="parecer-do-lote">Parecer ou justificativa *</Label>
              <Textarea
                id="parecer-do-lote"
                value={parecer}
                onChange={(evento) => setParecer(evento.target.value)}
                rows={3}
                placeholder="Descreva o motivo. Ele fica registrado em cada credenciado alcançado."
              />
            </div>
          )}

          {confirmacao?.acao === 'BLOQUEAR' && (
            <div className="space-y-2">
              <Label htmlFor="bloqueado-ate">Bloqueado até (opcional)</Label>
              <Input
                id="bloqueado-ate"
                type="date"
                value={bloqueadoAte}
                onChange={(evento) => setBloqueadoAte(evento.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                Em branco, o bloqueio não tem prazo e só sai com reativação manual.
              </p>
            </div>
          )}

          {confirmacao?.irreversivel && (
            <div className="border-destructive/40 bg-destructive/5 space-y-2 rounded border p-3">
              <Label
                htmlFor="palavra-de-confirmacao"
                className="text-destructive text-xs uppercase tracking-wider"
              >
                Confirmação de segurança
              </Label>
              <p className="text-muted-foreground text-xs">
                Digite <strong className="font-mono">{PALAVRA_DE_CONFIRMACAO}</strong> para liberar
                o botão. A ação não se desfaz clicando de novo.
              </p>
              <Input
                id="palavra-de-confirmacao"
                value={palavraDigitada}
                onChange={(evento) => setPalavraDigitada(evento.target.value)}
                placeholder={PALAVRA_DE_CONFIRMACAO}
                autoComplete="off"
                className="font-mono"
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmar}
              disabled={!liberado}
              className={
                confirmacao?.irreversivel
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              Confirmar e aplicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
